import {
  Heading,
  Label,
  Option,
  Select,
} from '@alefeducation/ui-elements-library-core';
import React from 'react';
import styled from 'styled-components';
import { ROLES } from '../../utils/constants';
import { flexStyle } from '../CommonStyles/TableStyles';
import { AdminRole } from '../../pages/AddAdminUser/hooks/useAddAdminUser';
import TenantDropdown from '../OrganisationEditorModal/TenantDropdown';
import { AdminOrg } from '../../pages/EditAdminUser/hooks/useGetAdminByIdState';
import { SearchPair } from '../Roles/hooks/useRoles';
import { OrganisationByTenantDropdown } from '../OrganisationsComponents';
import { StyledLabelContainer } from '../OrganisationEditorModal/OrganisationEditorModal';

const RoleSelectionContainer = styled.div`
  ${flexStyle};
  padding: ${({ theme }) => `${theme.space[40]}px ${theme.space[24]}px`};
`;

const StyledRow = styled.div`
  display: flex;
  column-gap: 24px;
  row-gap: 24px;
  flex-wrap: wrap;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 250px;
`;

const StyledSelect = styled(Select)<{ isEdit: boolean; isView: boolean }>`
  width: 100%;
  pointer-events: ${(props) => props.isEdit && props.isView && 'none'};
  opacity: ${(props) => props.isEdit && props.isView && '0.5'};
`;

type Props = {
  selectedRole: string;
  selectedTenant?: string;
  disabled?: boolean;
  isView?: boolean;
  isEdit?: boolean;
  selectedOrganisations?: AdminOrg[];
  onChange: (e: { target: { id: string; value: any } }) => void;
};

const roles = [
  AdminRole.SUPER_ADMIN_ORG,
  AdminRole.ORGANIZATION_ADMIN,
  AdminRole.EMARKING_ADMIN,
  AdminRole.ALEF_CONSOLE_SUPPORT,
  AdminRole.ALEF_ORG_SUPPORT,
];

const RolesSection = (props: Props) => {
  const {
    selectedRole,
    selectedTenant = '',
    selectedOrganisations = [],
    isView = true,
    isEdit = false,
    disabled = false,
    onChange,
  } = props;

  const onSelectChange = (testId: string, label: string, role?: string) => {
    onChange({
      target: {
        id: 'role',
        value: role || '',
      },
    });
  };

  const onTenantChange = (tenant: string) => {
    onChange({
      target: {
        id: 'tenant',
        value: tenant || '',
      },
    });
  };

  const onOrganisationChange = (orgs: SearchPair[]) => {
    onChange({
      target: {
        id: 'organisations',
        value: orgs.map((o) => ({ code: o.key, name: o.value })) || [],
      },
    });
  };

  return (
    <RoleSelectionContainer>
      <Heading
        data-testid="roles-section-heading"
        variant="heading-small"
        color={disabled ? 'primary.40' : 'text.100'}
      >
        {ROLES}
      </Heading>

      {!disabled && (
        <StyledRow>
          <StyledColumn>
            <StyledLabelContainer>
              <Label title="Role" />
            </StyledLabelContainer>
            <StyledSelect
              data-testid="select-roles"
              onSelectChange={onSelectChange}
              placeholder="— Select —"
              selectedOption={selectedRole}
              isEdit={isEdit}
              isView={isView}
              dropdownPosition="top"
              variant="default"
            >
              {roles.map((role) => (
                <Option
                  selected={role === selectedRole}
                  value={role}
                  key={role}
                >
                  {role}
                </Option>
              ))}
            </StyledSelect>
          </StyledColumn>

          {(selectedRole === AdminRole.ORGANIZATION_ADMIN ||
            selectedRole === AdminRole.ALEF_ORG_SUPPORT) && (
            <React.Fragment>
              <TenantDropdown
                width="250px"
                onChange={onTenantChange}
                selectedTenantCode={selectedTenant}
              />

              {selectedTenant !== '' && (
                <OrganisationByTenantDropdown
                  selectedTenantCode={selectedTenant}
                  onChange={onOrganisationChange}
                  selectedOrganisations={selectedOrganisations}
                />
              )}
            </React.Fragment>
          )}
        </StyledRow>
      )}
    </RoleSelectionContainer>
  );
};

export default RolesSection;
